import { BANDS, type Band } from '@badger-sentinel/rules';
import type { CellViewWire } from '@badger-sentinel/protocol';

import { compareAliases } from './alias.js';
import type { Selection } from './selection.js';

/**
 * The orbital board's half of the projection — everything the ring cannot draw.
 *
 * `vendor/maps/orbital-map.js` places a marker at one angle on one ring, and it
 * derives that angle from a slot. That is the whole of its vocabulary, and it
 * gives this phase one rule:
 *
 * > **A marker on a ring means the wire named a slot.**
 *
 * Three of the wire's locations do not name one, and all three are ordinary
 * rather than exceptional — in turn 1 of the only scenario, *every* hostile in
 * space is one of them:
 *
 * | Wire | What it says | Why the ring cannot hold it |
 * | --- | --- | --- |
 * | `contacts[].location = {kind:'BAND'}` | somewhere in GEO | every position the renderer can draw is a slot position, so any of them would claim a bin the seat has not earned (must-not #1) |
 * | `friendly[].location = {kind:'TRANSIT'}` | between two bands, arriving on turn *n* | the destination ring is where it will be, and drawing it there says it has arrived (must-not #4) |
 * | `contacts[].location = {kind:'UNKNOWN'}` | detected, not placed | there is no band to draw it in — and dropping it would render an unknown as nothing (must-not #3) |
 *
 * So they are drawn where this console owns the pixels: the **band strip** under
 * the canvas (`sda/BandStrip.tsx`), one cell per band, each naming what the band
 * holds that the ring is not showing and why. It is the same answer Phase 5 gave
 * the relocation arrow — *a sentence that is right beats a mark that is wrong* —
 * and it is the only one available here, because the alternative is a slot
 * number nobody sent.
 *
 * ## The debris number lives here too, and that is the renderer's own ruling
 *
 * `orbital-map.js`'s header: *"Altitudes, debris levels and rosters are the
 * rails' job and are not repeated here"*, and its ring-label pass says the same
 * thing a second time — the labels were cut back to the regime name alone
 * because altitude and debris *"made the labels the widest objects on the
 * canvas"*. Debris is per band, it is a number, and it prices a roll rather than
 * a position (`model/ladder.ts`: `CONJUNCTION`, never a hit chance). So it sits
 * in the strip's own cell, beside the slot count, and in the selection rail's
 * `EnvironmentBlock` when a band is selected. It is never drawn on a ring.
 *
 * ## Nothing here computes a distance
 *
 * Band separation is a rule table (`bandSeparation`, `SDA_SAT_ONE_BAND_AWAY_MODIFIER`)
 * and ring spacing is set by slot arc length — the renderer says so at its line
 * 47. This module reads `map.bandSlots` and `theatre.debris` and nothing else
 * spatial (must-not #6).
 */

/** The heading a group of unplaced objects sits under. One clause, no sentence. */
export type UnplacedReason = 'SLOT UNKNOWN' | 'IN TRANSIT' | 'LOCATION UNKNOWN';

export interface UnplacedRow {
  readonly id: string;
  readonly side: 'BLU' | 'OPF';
  /** What the wire *does* say about it. The rung, or the turn it arrives. */
  readonly detail: string;
  readonly selects: Selection;
}

export interface UnplacedGroup {
  readonly reason: UnplacedReason;
  readonly rows: readonly UnplacedRow[];
}

export interface BandRow {
  /** A band, or `UNKNOWN` for the column that holds contacts the wire cannot place. */
  readonly band: string;
  /** `map.bandSlots` — 24 everywhere but HEO. `null` in the `UNKNOWN` column. */
  readonly slots: number | null;
  /** `theatre.debris` for this band, 0–5. `null` in the `UNKNOWN` column. */
  readonly debris: number | null;
  /**
   * What the band holds that the ring is not showing, grouped by why.
   *
   * What it is *not* is a second count of what the ring **is** showing. Those
   * markers are on the canvas an inch above, and the selection rail states the
   * numbers when a band is selected — a strip that repeated them would be the
   * console saying one thing three times.
   */
  readonly unplaced: readonly UnplacedGroup[];
}

/** The column that holds what no band does. Not a `Band`, deliberately. */
export const UNPLACED_BAND = 'UNKNOWN';

/**
 * One row per band, plus an `UNKNOWN` column when the wire has one to fill it.
 *
 * The four bands are always present, in `BANDS` order, whether or not they hold
 * anything: an empty ring is a statement — *nothing of yours and nothing of
 * theirs is up there* — and a strip that hid it would leave the operator to
 * infer emptiness from an absence, which is the one reading an absence cannot
 * carry. The fifth column appears only when there is something in it, because a
 * heading over nothing is not a statement about anything.
 */
export function bandRows(view: CellViewWire): readonly BandRow[] {
  const debris = view.theatre.debris as Readonly<Record<string, number>>;
  const slots = view.map.bandSlots as Readonly<Record<string, number>>;

  const rows: BandRow[] = BANDS.map((band) => ({
    band,
    slots: slots[band] ?? null,
    debris: debris[band] ?? null,
    unplaced: groupsIn(view, band),
  }));

  const nowhere = unknownContacts(view);
  if (nowhere.length > 0) {
    rows.push({
      band: UNPLACED_BAND,
      slots: null,
      debris: null,
      unplaced: [{ reason: 'LOCATION UNKNOWN', rows: nowhere }],
    });
  }
  return rows;
}

/**
 * What this band holds that the ring is not showing, grouped by why.
 *
 * `SLOT UNKNOWN` first, because it is the enemy picture and it is what an SDA
 * seat is here to close; `IN TRANSIT` after it, because it is a thing already
 * decided and merely not finished.
 */
function groupsIn(view: CellViewWire, band: Band): readonly UnplacedGroup[] {
  const contacts = view.contacts
    .filter((contact) => contact.location.kind === 'BAND' && contact.location.band === band)
    .map((contact): UnplacedRow => ({
      id: contact.id,
      side: 'OPF',
      // The rung *is* the reason there is no slot: FOUND means detected and
      // not localised. Naming it says why the row is here without a caption.
      detail: contact.rung,
      selects: { kind: 'CONTACT', id: contact.id },
    }));

  const transit = view.friendly
    .filter((asset) => asset.location.kind === 'TRANSIT' && asset.location.to === band)
    .map((asset): UnplacedRow => {
      const location = asset.location;
      return {
        id: asset.id,
        side: 'BLU',
        detail:
          location.kind === 'TRANSIT' ? location.from + ' → T' + String(location.arrivesTurn) : '',
        selects: { kind: 'ASSET', id: asset.id },
      };
    });

  const groups: UnplacedGroup[] = [];
  if (contacts.length > 0) groups.push({ reason: 'SLOT UNKNOWN', rows: byAlias(contacts) });
  if (transit.length > 0) groups.push({ reason: 'IN TRANSIT', rows: byAlias(transit) });
  return groups;
}

function unknownContacts(view: CellViewWire): readonly UnplacedRow[] {
  return byAlias(
    view.contacts
      .filter((contact) => contact.location.kind === 'UNKNOWN')
      .map((contact) => ({
        id: contact.id,
        side: 'OPF' as const,
        detail: contact.rung,
        selects: { kind: 'CONTACT' as const, id: contact.id },
      })),
  );
}

/**
 * Ordered by the alias hash, never by arrival (must-not #5, `model/alias.ts`).
 *
 * The strip is a list of contacts rendered in a fixed order, which is exactly
 * the shape a wire ordering could leak through — so it takes the same ordering
 * the canvas takes, for the same reason.
 */
function byAlias(rows: readonly UnplacedRow[]): readonly UnplacedRow[] {
  return [...rows].sort((a, b) => compareAliases(a.id, b.id));
}

/**
 * The cross-domain link: the selected satellite and the station that flies it.
 *
 * `friendly[].ttcStation` is a ground asset id, and the renderer's
 * `setCrossLink({spaceId, groundLabel})` draws a dashed line from the marker to
 * a neutral plate reading `GROUND · <label>`. Neutral rather than selected or
 * armed, which is the renderer's own note: it is a reference, not a subject.
 *
 * Only for a satellite the ring is actually drawing — `drawCrossLink` projects
 * `u.slot`, and an asset in transit has none, so a link to it would be a line to
 * `NaN`. And only for the selection, because the link answers *"who flies this
 * one"*, which is a question about the thing in hand.
 */
export function crossLinkFor(
  view: CellViewWire,
  selection: Selection | null,
): { readonly spaceId: string; readonly groundLabel: string } | null {
  if (selection === null || selection.kind !== 'ASSET') return null;
  const asset = view.friendly.find((candidate) => candidate.id === selection.id);
  if (asset === undefined || asset.location.kind !== 'ORBIT') return null;
  if (asset.ttcStation === null) return null;
  return { spaceId: asset.id, groundLabel: asset.ttcStation };
}

/** Is this id one of the wire's four bands? The strip's fifth column is not. */
export function isBand(id: string): id is Band {
  return (BANDS as readonly string[]).includes(id);
}
